import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Tabs, Tab } from 'react-bootstrap';
import api from '../../services/api';

const SupplyModal = ({
  show,
  onHide,
  service,                // 'wb' | 'ozon'
  orderPositions,         // весь список
  selectedPositionIds,    // выделенные позиции
  afterSuccess,           // коллбек после успешной операции (обычно onHide)
}) => {
  const [tab, setTab] = useState('existing'); // existing | create
  const [supplies, setSupplies] = useState([]);
  const [selectedSupplyId, setSelectedSupplyId] = useState(null);
  const [newSupplyName, setNewSupplyName] = useState('');
  const [loading, setLoading] = useState(false);

  const selectedOrderIdsForOzon = useMemo(() => {
    // из выбранных позиций получаем уникальные order.id
    const ids = new Set();
    (orderPositions || []).forEach(p => {
      if (selectedPositionIds.includes(p.id) && p.order?.id) {
        ids.add(p.order.id);
      }
    });
    return Array.from(ids);
  }, [orderPositions, selectedPositionIds]);

  useEffect(() => {
    if (!show) return;
    const fetchSupplies = async () => {
      try {
        const res = await api.get(`/supplies/${service}`);
        setSupplies(res.data || []);
      } catch (e) {
        console.error('Ошибка загрузки поставок:', e);
      } finally {
        setSelectedSupplyId(null);
      }
    };
    fetchSupplies();
    setTab('existing');
    setNewSupplyName('');
  }, [show, service]);

  const patchAddToSupply = async (supplyId) => {
    const payload =
      service === 'wb'
        ? {
            supplyId,
            orderIds: [],
            orderPositionIds: selectedPositionIds.map(String),
          }
        : {
            supplyId,
            orderIds: selectedOrderIdsForOzon.map(String),
            orderPositionIds: [],
          };

    await api.patch(`/supplies/${service}`, payload);
  };

  const handleAddToExisting = async () => {
    if (!selectedSupplyId) return;
    setLoading(true);
    try {
      await patchAddToSupply(selectedSupplyId);
      afterSuccess?.();
    } catch (e) {
      console.error('Ошибка при добавлении к поставке:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAndAdd = async () => {
    const name = newSupplyName.trim();
    if (!name) return;

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      const created = await api.post(`/supplies/${service}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newSupply = created.data;
      if (!newSupply?.id) throw new Error('Поставка создана без id');
      await patchAddToSupply(newSupply.id);
      afterSuccess?.();
    } catch (e) {
      console.error('Ошибка при создании или добавлении:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Поставка ({service.toUpperCase()})</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs activeKey={tab} onSelect={(k) => setTab(k)}>
          <Tab eventKey="existing" title="Существующие">
            <div className="mt-3">
              {supplies.length === 0 && <div className="text-muted">Нет поставок</div>}
              {supplies.map((s) => (
                <div key={s.id} className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="supply"
                    value={s.id}
                    id={`supply-${s.id}`}
                    checked={selectedSupplyId === s.id}
                    onChange={() => setSelectedSupplyId(s.id)}
                  />
                  <label className="form-check-label" htmlFor={`supply-${s.id}`}>
                    {s.name} {s.time && `(${new Date(s.time).toLocaleString()})`}
                  </label>
                </div>
              ))}
            </div>
          </Tab>

          <Tab eventKey="create" title="Создать новую">
            <div className="mt-3">
              <div className="mb-3">
                <label className="form-label">Название поставки</label>
                <input
                  type="text"
                  className="form-control"
                  value={newSupplyName}
                  onChange={(e) => setNewSupplyName(e.target.value)}
                  placeholder="Например: Поставка 20.09"
                />
              </div>
              <div className="text-muted">
                После создания к новой поставке будут добавлены выбранные позиции
                {service === 'ozon' ? ' (по заказам).' : '.'}
              </div>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        {tab === 'existing' ? (
          <>
            <Button variant="secondary" onClick={onHide} disabled={loading}>
              Отмена
            </Button>
            <Button variant="success" onClick={handleAddToExisting} disabled={!selectedSupplyId || loading}>
              Добавить к выбранной
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onHide} disabled={loading}>
              Отмена
            </Button>
            <Button variant="primary" onClick={handleCreateAndAdd} disabled={!newSupplyName.trim() || loading}>
              Создать и добавить
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SupplyModal;
